import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { useApp } from '../context/AppContext';

const Product360Viewer = ({ productId, productName, mainImage, API }) => {
  const { t } = useApp();
  const [has360View, setHas360View] = useState(false);
  const [gifUrl, setGifUrl] = useState(null);
  const [views, setViews] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentAngle, setCurrentAngle] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    load360View();
  }, [productId]);

  const load360View = async () => {
    try {
      // Get 360 view data
      const response = await fetch(`${API}/products/${productId}/360-views`);
      const data = await response.json();
      
      setHas360View(data.has_360_view);
      
      if (data.rotation_gif) {
        // New GIF format
        setGifUrl(data.rotation_gif);
      } else if (data.views && data.views.length > 0) {
        // Old format with separate images
        setViews(data.views);
      }
    } catch (error) {
      console.error('Error loading 360 view:', error);
      setHas360View(false);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get full image URL
  const getImageUrl = (url) => {
    if (!url) return mainImage;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const backendUrl = process.env.REACT_APP_BACKEND_URL || API.replace('/api', '');
    return `${backendUrl}${url}`;
  };

  // Drag handlers for manual rotation (only for non-GIF mode)
  const handleMouseDown = (e) => {
    if (gifUrl) return; // Don't handle drag for GIF
    setIsDragging(true);
    setStartX(e.clientX || e.touches?.[0]?.clientX || 0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || views.length === 0 || gifUrl) return;
    
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const diff = currentX - startX;
    
    if (Math.abs(diff) > 50) {
      const direction = diff > 0 ? -1 : 1;
      setCurrentAngle((prev) => (prev + direction + views.length) % views.length);
      setStartX(currentX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const nextView = () => {
    setCurrentAngle((prev) => (prev + 1) % views.length);
  };

  const prevView = () => {
    setCurrentAngle((prev) => (prev - 1 + views.length) % views.length);
  };

  // While loading, show nothing (will render button once loaded)
  if (loading) {
    return null;
  }

  // If no 360 view available, show disabled button
  if (!has360View || (!gifUrl && views.length === 0)) {
    return (
      <button
        className="absolute bottom-4 right-4 p-2 glass rounded-full opacity-50 cursor-not-allowed"
        disabled
        title="360° weergave niet beschikbaar"
        data-testid="360-view-btn-disabled"
      >
        <RotateCw size={20} className="text-[#A8A29E]" />
      </button>
    );
  }

  return (
    <>
      {/* 360 Button - Always clickable if has360View */}
      <button
        onClick={() => setIsOpen(true)}
        className="absolute bottom-4 right-4 p-2 glass rounded-full hover:bg-white/10 transition-colors group"
        data-testid="360-view-btn"
        title="360° weergave bekijken"
      >
        <RotateCw size={20} className="text-[#D4AF37] group-hover:animate-spin" />
      </button>

      {/* 360 Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md"
          onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
        >
          <div className="relative max-w-3xl w-full mx-4">
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute -top-14 right-0 p-3 bg-white/10 rounded-full text-white hover:text-[#D4AF37] hover:bg-white/20 transition-all"
              data-testid="360-close-btn"
            >
              <X size={24} />
            </button>

            {/* Title */}
            <div className="text-center mb-6">
              <h3 className="text-white font-heading text-2xl mb-1">
                {productName}
              </h3>
              <p className="text-[#D4AF37] text-sm flex items-center justify-center gap-2">
                <RotateCw size={14} className={gifUrl ? 'animate-spin' : ''} />
                {t('rotating_view') || '360° Draaiende Weergave'}
              </p>
            </div>

            {/* Viewer */}
            {gifUrl ? (
              /* GIF Mode - Automatic rotation */
              <div className="relative aspect-square bg-gradient-to-b from-[#1C1917] to-[#0A0A0A] rounded-2xl overflow-hidden shadow-2xl border border-[#2C1B18]">
                <img
                  src={getImageUrl(gifUrl)}
                  alt={`${productName} - 360° rotatie`}
                  className="w-full h-full object-contain select-none"
                  draggable={false}
                  data-testid="360-gif-image"
                />

                {/* Golden glow effect */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#D4AF37]/10 to-transparent" />
                </div>

                {/* Status badge */}
                <div className="absolute top-4 left-4 px-3 py-1.5 glass rounded-full border border-[#D4AF37]/30">
                  <span className="text-[#D4AF37] text-xs font-medium flex items-center gap-1.5">
                    <RotateCw size={12} className="animate-spin" />
                    {t('rotating_auto') || 'Draait automatisch'}
                  </span>
                </div>
              </div>
            ) : (
              /* Manual drag/click rotation mode */
              <div
                ref={containerRef}
                className="relative aspect-square bg-gradient-to-b from-[#1C1917] to-[#0A0A0A] rounded-2xl overflow-hidden shadow-2xl border border-[#2C1B18] cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleMouseUp}
              >
                {/* Current View */}
                <img
                  src={getImageUrl(views[currentAngle]?.url)}
                  alt={`${productName} - ${views[currentAngle]?.angle || 0}°`}
                  className="w-full h-full object-contain select-none"
                  draggable={false}
                />

                {/* Navigation Arrows */}
                <button
                  onClick={(e) => { e.stopPropagation(); prevView(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 glass rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft size={24} className="text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextView(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 glass rounded-full hover:bg-white/20 transition-colors"
                >
                  <ChevronRight size={24} className="text-white" />
                </button>

                {/* Angle Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 glass rounded-full">
                  <span className="text-[#D4AF37] text-sm font-medium">
                    {views[currentAngle]?.angle || 0}°
                  </span>
                </div>

                {/* Drag Hint */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 glass rounded-full opacity-75">
                  <span className="text-white text-xs flex items-center gap-1">
                    <RotateCw size={12} />
                    Sleep om te draaien
                  </span>
                </div>
              </div>
            )}

            {/* View Dots (only for manual mode) */}
            {!gifUrl && views.length > 0 && (
              <div className="flex justify-center gap-2 mt-4">
                {views.map((view, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentAngle(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentAngle
                        ? 'bg-[#D4AF37] scale-110'
                        : 'bg-[#57534E] hover:bg-[#D4AF37]/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Info text */}
            <p className="text-center text-[#57534E] text-xs mt-4">
              {gifUrl 
                ? (t('ai_generated_360') || 'AI-gegenereerde 360° weergave met luxe achtergrond')
                : 'Sleep of klik op de pijlen om te draaien'
              }
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Product360Viewer;
